import React, { useEffect, useMemo, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { HelpCircle, Clock } from 'lucide-react';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import { buildFaqPrograms } from '../data/faqData';
import { usePaidAssessment } from '../utils/usePublicConfig';

const SITE_NAME = 'Orosz Coaching';

export default function FaqPage() {
  const paidAssessment = usePaidAssessment();
  const FAQ_PROGRAMS = useMemo(() => buildFaqPrograms(paidAssessment), [paidAssessment]);
  const firstActive = FAQ_PROGRAMS.find((p) => !p.comingSoon);
  const [selected, setSelected] = useState(firstActive?.id || null);

  useDocumentMeta({
    title: `GYIK — Gyakran ismételt kérdések | ${SITE_NAME}`,
    description:
      'Gyakran ismételt kérdések az Orosz Coaching programjairól: Életmódváltó Program, Izomépítés és formázás, Kiscsoportos Személyi Edzés és 1:1 Online Mentorálás — árak, felmérés, lemondás, étrend.',
    canonical: `${typeof window !== 'undefined' ? window.location.origin : ''}/gyik`,
    type: 'website',
  });

  // FAQPage JSON-LD a Google-nek (csak az aktív programok kérdései)
  useEffect(() => {
    const items = FAQ_PROGRAMS.filter((p) => !p.comingSoon).flatMap((p) => p.faqs);
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'faq-jsonld';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: items.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.head.appendChild(script);
    return () => { document.getElementById('faq-jsonld')?.remove(); };
  }, []);

  const active = FAQ_PROGRAMS.find((p) => p.id === selected);

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="faq-page">
      <Header />

      {/* Hero */}
      <section className="relative pt-32 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-900/20 via-gray-950 to-gray-950 pointer-events-none" />
        <div className="container mx-auto px-6 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-yellow-500/10 border border-yellow-600/30 rounded-full">
              <HelpCircle size={14} className="text-yellow-500" />
              <span className="text-yellow-500 font-semibold text-xs tracking-widest uppercase">GYIK</span>
            </div>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Gyakran ismételt <span className="text-yellow-500">kérdések</span>
            </h1>
            <p className="text-base md:text-lg text-gray-400">
              Összegyűjtöttem a kérdéseket, amiket a legtöbbször megkapok. Válaszd ki, melyik program
              érdekel, és ott azonnal látod a válaszokat — ha mégsem találod a kérdésed, írj bátran.
            </p>
          </div>
        </div>
      </section>

      {/* Program választó gombok */}
      <section className="container mx-auto px-6 pb-6">
        <div className="flex flex-col sm:flex-row flex-wrap gap-3">
          {FAQ_PROGRAMS.map((p) => (
            <button
              key={p.id}
              data-testid={`faq-program-btn-${p.id}`}
              disabled={p.comingSoon}
              onClick={() => setSelected(p.id)}
              className={
                'relative rounded-xl px-5 py-4 text-left font-bold text-sm md:text-base transition-all duration-200 border ' +
                (p.comingSoon
                  ? 'bg-gray-900 border-gray-800 text-gray-500 cursor-not-allowed'
                  : selected === p.id
                    ? 'bg-yellow-500 border-yellow-500 text-gray-900 shadow-lg shadow-yellow-500/20'
                    : 'bg-yellow-600/90 border-yellow-600 text-gray-900 hover:bg-yellow-500 hover:border-yellow-500')
              }
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {p.button}
              {p.comingSoon && (
                <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-gray-800 text-gray-400 px-2 py-0.5 rounded-full align-middle">
                  <Clock size={10} /> Hamarosan
                </span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Kérdés-válaszok */}
      <section className="container mx-auto px-6 pb-20">
        {active && !active.comingSoon ? (
          <div data-testid="faq-answers" className="max-w-3xl">
            <h2 className="text-base md:text-lg font-bold text-gray-300 mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              {active.title}
            </h2>
            <Accordion type="single" collapsible className="space-y-2">
              {active.faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${active.id}-${i}`}
                  className="rounded-xl border border-gray-800 bg-gray-900/60 px-5 data-[state=open]:border-yellow-600/50"
                >
                  <AccordionTrigger className="text-left text-sm md:text-base font-semibold text-white hover:text-yellow-400 hover:no-underline py-4">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-gray-300 leading-relaxed pb-5">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
            <div className="mt-8 rounded-xl border border-yellow-600/30 bg-yellow-500/5 p-5 text-sm text-gray-300">
              Nem találod a kérdésed?{' '}
              <a href="/#contact" className="text-yellow-500 font-semibold hover:text-yellow-400 underline underline-offset-2">
                Írj nekem itt
              </a>{' '}
              — vagy foglalj egy ingyenes konzultációt, és átbeszéljük, melyik forma illik hozzád a legjobban. Személyesen a BB Gymben vagy online – ahogy neked kényelmes.
            </div>
          </div>
        ) : (
          <div className="text-gray-500 text-sm py-8" data-testid="faq-coming-soon">
            Ennek a programnak a kérdései hamarosan elérhetők.
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
